/*
Copyright 2020 Adobe
All Rights Reserved.

NOTICE: Adobe permits you to use, modify, and distribute this file in
accordance with the terms of the Adobe license agreement accompanying
it. If you have received this file from a source other than Adobe,
then your use, modification, or distribution of it requires the prior
written permission of Adobe.
*/

class ViewSDKClient {
  constructor() {
    this.readyPromise = new Promise(resolve => {
      if (window.AdobeDC) {
        resolve();
      } else {
        /* Wait for Adobe Document Services PDF Embed API to be ready */
        document.addEventListener('adobe_dc_view_sdk.ready', () => {
          resolve();
        });
      }
    });
    this.adobeDCView = undefined;
    this.reportView = undefined;
    this.previewPromise = undefined;
    this.state = { file: undefined };
    this.isDisposed = false;
    this.activeDivId = undefined;
    /** Last page reported by PAGE_VIEW; suppresses redundant gotoLocation calls. */
    this.viewerReportedPage = null;
  }

  ready() {
    return this.readyPromise;
  }

  base64ToArrayBuffer(pdfData) {
    if (pdfData instanceof ArrayBuffer) {
      return pdfData;
    }

    if (ArrayBuffer.isView(pdfData)) {
      return pdfData.buffer;
    }

    var bin = atob(pdfData);
    var len = bin.length;
    var uInt8Array = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
      uInt8Array[i] = bin.charCodeAt(i);
    }
    return uInt8Array.buffer;
  }

  shouldProgrammaticGoto(page) {
    const normalized = Number(page);
    if (!Number.isFinite(normalized) || normalized < 1) {
      return false;
    }
    return this.viewerReportedPage !== normalized;
  }

  goToViewedPage(previewFilePromise, currentPage) {
    if (!this.shouldProgrammaticGoto(currentPage)) {
      return;
    }

    this.viewerReportedPage = null;

    previewFilePromise.then(adobeViewer => {
      adobeViewer.getAPIs().then(apis => {
        apis.gotoLocation(currentPage);
      });
    });
  }

  processEvent(event, setCurrentPage) {
    if (event.type === 'PAGE_VIEW') {
      const page = Number(event.data.pageNumber);
      if (Number.isFinite(page) && page >= 1) {
        this.viewerReportedPage = page;
        setCurrentPage(page);
      }
    }
  }

  previewFile(divId, viewerConfig, pdfData) {
    this.isDisposed = false;
    this.activeDivId = divId;
    this.viewerReportedPage = null;
    const config = {
      /* Pass your registered client id */
      clientId: process.env.REACT_APP_ADOBE_CLIENT_ID,
    };
    if (divId) {
      /* Optional only for Light Box embed mode */
      /* Pass the div id in which PDF should be rendered */
      config.divId = divId;
    }
    /* Initialize the AdobeDC View object */
    this.adobeDCView = new window.AdobeDC.View(config);

    const fileName =
      pdfData?.data?.document?.documentType?.name ||
      pdfData?.data?.document?.documentName ||
      'Document';

    /* Invoke the file preview API on Adobe DC View object */
    const documentBody = pdfData?.data?.document?.documentBody;
    const contentPromise = documentBody instanceof Blob
      ? documentBody.arrayBuffer()
      : Promise.resolve(this.base64ToArrayBuffer(documentBody));

    const previewFilePromise = this.adobeDCView.previewFile(
      {
        /* Pass information on how to access the file */
        content: {
          promise: contentPromise,
        },
        /* Pass meta data of file */
        metaData: {
          /* file name */
          fileName,
          /* file ID */
        },
      },
      viewerConfig,
    );

    this.previewPromise = previewFilePromise;
    return previewFilePromise;
  }

  previewFileUsingFilePromise(divId, viewerConfig, reportData) {
    /* Initialize the AdobeDC View object */
    const config = {
      /* Pass your registered client id */
      clientId: process.env.REACT_APP_ADOBE_CLIENT_ID,
    };
    if (divId) {
      /* Optional only for Light Box embed mode */
      /* Pass the div id in which PDF should be rendered */
      config.divId = divId;
    }
    this.reportView = new window.AdobeDC.View(config);

    const getBase64 = async file => {
      return new Promise(resolve => {
        let baseURL = '';
        // Make new FileReader
        let reader = new FileReader();

        // Convert the file to base64 text
        reader.readAsDataURL(file);

        // on reader load somthing...
        reader.onload = () => {
          // Make a fileInfo Object
          baseURL = reader.result;
          resolve(baseURL);
        };
      });
    };
    const file = reportData?.data;
    getBase64(file)
      .then(result => {
        file['base64'] = result;
        // console.log('File Is', file);
        const temp = file.base64;
        this.state.file = temp && temp.replace('data:application/pdf;base64,', '');

        const previewFilePromise = this.reportView.previewFile(
          {
            /* Pass information on how to access the file */
            content: {
              /* pass file promise which resolve to arrayBuffer */
              promise: Promise.resolve(this.base64ToArrayBuffer(this.state.file)),
            },
            /* Pass meta data of file */
            metaData: {
              /* file name */
              fileName: 'Report',
            },
          },
          viewerConfig,
        );
        return previewFilePromise;
      })
      .catch(err => {
        console.log(err);
      });
  }

  registerSaveApiHandler() {
    /* Define Save API Handler */
    const saveApiHandler = (metaData, content, options) => {
      console.log(metaData, content, options);
      return new Promise(resolve => {
        /* Dummy implementation of Save API, replace with your business logic */
        setTimeout(() => {
          const response = {
            code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
            data: {
              metaData: Object.assign(metaData, { updatedAt: new Date().getTime() }),
            },
          };
          resolve(response);
        }, 2000);
      });
    };

    this.adobeDCView.registerCallback(
      window.AdobeDC.View.Enum.CallbackType.SAVE_API,
      saveApiHandler,
      {},
    );
  }

  registerEventsHandler(setCurrentPage) {
    if (!this.adobeDCView || this.isDisposed) {
      return;
    }

    /* Register the callback to receive the events */
    this.adobeDCView.registerCallback(
      /* Type of call back */
      window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER,
      /* call back function */
      event => {
        this.processEvent(event, setCurrentPage);
      },
      /* options to control the callback execution */
      {
        /* Enable PDF analytics events on user interaction. */
        enablePDFAnalytics: true,
      },
    );
  }

  cleanup(divId) {
    if (divId && this.activeDivId && divId !== this.activeDivId) {
      const staleContainer = document.getElementById(divId);
      if (staleContainer) {
        staleContainer.replaceChildren();
      }
      return;
    }

    const container = document.getElementById(divId);
    if (container) {
      container.replaceChildren();
    }

    this.isDisposed = true;
    this.previewPromise = undefined;
    this.adobeDCView = undefined;
    this.reportView = undefined;
    this.activeDivId = undefined;
  }
}

export default ViewSDKClient;
